import { Link, useNavigate, useParams, useSearch } from "@tanstack/solid-router";
import { Show, createSignal, For } from "solid-js";

import { AccountPicker } from "~/components/AccountPicker";
import { AppShell } from "~/components/AppShell";
import { ContactsBulkBar } from "~/components/ContactsBulkBar";
import { CsvIoButtons } from "~/components/CsvIoButtons";
import { LeadScoreBadge } from "~/components/LeadScoreBadge";
import { SavedViewsBar } from "~/components/SavedViewsBar";
import { TagBadges, TagsEditor } from "~/components/TagsEditor";
import { UserPicker } from "~/components/UserPicker";
import { useContactsInfinite, useCreateContact, useMe } from "~/lib/hooks";
import { useRowSelection } from "~/lib/selection";
import type { ContactListItem } from "~/lib/types";

type Band = "all" | "hot" | "warm" | "cool" | "cold" | "unscored";

const BAND_RANGES: Record<Band, { min?: number; max?: number; unscored?: boolean }> = {
  all: {},
  hot: { min: 80 },
  warm: { min: 50, max: 79 },
  cool: { min: 20, max: 49 },
  cold: { min: 0, max: 19 },
  unscored: { unscored: true },
};

export function ContactsPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const navigate = useNavigate();
  // Read `?band=hot` from the URL so the dashboard "Hot leads" → "View
  // all" link lands on the right band.
  const urlSearch = useSearch({ strict: false }) as () => { band?: string };

  const [q, setQ] = createSignal("");
  const [stage, setStage] = createSignal("");
  const [tagFilter, setTagFilter] = createSignal("");
  const [ownerFilter, setOwnerFilter] = createSignal<string | null>(null);
  const [ownerFilterName, setOwnerFilterName] = createSignal<string | null>(null);
  const [band, setBand] = createSignal<Band>((urlSearch().band as Band) ?? "all");
  const [showCreate, setShowCreate] = createSignal(false);
  const [createTags, setCreateTags] = createSignal<string[]>([]);
  const [createOwner, setCreateOwner] = createSignal<string | null>(null);
  const [createOwnerName, setCreateOwnerName] = createSignal<string | null>(null);
  const me = useMe();

  const contacts = useContactsInfinite(
    () => params().wsKey,
    () => {
      const range = BAND_RANGES[band()];
      return {
        q: q() || undefined,
        lifecycle_stage: stage() || undefined,
        tag: tagFilter() || undefined,
        owner_user_id: ownerFilter() ?? undefined,
        lead_score_min: range.min,
        lead_score_max: range.max,
        unscored_only: range.unscored,
        limit: 50,
      };
    },
  );

  const visibleRows = () => (contacts.data?.pages ?? []).flatMap((p) => p.items);
  const selection = useRowSelection<ContactListItem>(() => visibleRows().map((r) => r.id));

  const createMutation = useCreateContact(() => params().wsKey);
  const [primaryAccountId, setPrimaryAccountId] = createSignal<string | null>(null);
  const [primaryAccountName, setPrimaryAccountName] = createSignal<string | null>(null);

  const resetCreate = () => {
    setPrimaryAccountId(null);
    setPrimaryAccountName(null);
    setCreateTags([]);
    setCreateOwner(null);
    setCreateOwnerName(null);
  };

  const handleCreate = async (e: SubmitEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    const first = String(data.get("first_name") ?? "").trim();
    const last = String(data.get("last_name") ?? "").trim();
    if (!first && !last) return;
    const c = await createMutation.mutateAsync({
      first_name: first || undefined,
      last_name: last || undefined,
      primary_email: String(data.get("primary_email") ?? "") || undefined,
      primary_phone: String(data.get("primary_phone") ?? "") || undefined,
      lifecycle_stage: String(data.get("lifecycle_stage") ?? "lead"),
      primary_account_id: primaryAccountId() ?? undefined,
      tags: createTags().length > 0 ? createTags() : undefined,
      owner_user_id: createOwner() ?? undefined,
    });
    setShowCreate(false);
    resetCreate();
    void navigate({
      to: "/workspaces/$wsKey/contacts/$conId",
      params: { wsKey: params().wsKey, conId: c.id },
    });
  };

  return (
    <AppShell
      wsKey={params().wsKey}
      pageTitle="Contacts"
      rightSlot={
        <div class="flex items-center gap-1">
          <CsvIoButtons wsKey={() => params().wsKey} plural="contacts" />
          <button class="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
            New contact
          </button>
        </div>
      }
    >
      <div class="flex flex-col gap-4">
        <SavedViewsBar
          wsKey={() => params().wsKey}
          entityType={() => "contact"}
          currentFilters={() => ({
            q: q() || undefined,
            lifecycle_stage: stage() || undefined,
            tag: tagFilter() || undefined,
            owner_user_id: ownerFilter() ?? undefined,
            owner_user_name: ownerFilterName() ?? undefined,
          })}
          onApply={(f) => {
            setQ((f.q as string) ?? "");
            setStage((f.lifecycle_stage as string) ?? "");
            setTagFilter((f.tag as string) ?? "");
            setOwnerFilter((f.owner_user_id as string) ?? null);
            setOwnerFilterName((f.owner_user_name as string) ?? null);
          }}
          onClear={() => {
            setQ("");
            setStage("");
            setTagFilter("");
            setOwnerFilter(null);
            setOwnerFilterName(null);
          }}
        />
        <div
          class="flex gap-1 flex-wrap items-center text-sm"
          role="group"
          aria-label="Lead score band"
        >
          <span class="opacity-60 mr-1">Score:</span>
          <For
            each={
              [
                ["all", "All"],
                ["hot", "🔥 Hot"],
                ["warm", "Warm"],
                ["cool", "Cool"],
                ["cold", "Cold"],
                ["unscored", "Unscored"],
              ] as const
            }
          >
            {([key, label]) => (
              <button
                type="button"
                class={`btn btn-xs ${band() === key ? "btn-primary" : "btn-ghost"}`}
                onClick={() => setBand(key as Band)}
                aria-pressed={band() === key}
              >
                {label}
              </button>
            )}
          </For>
        </div>
        <div class="flex gap-2 flex-wrap">
          <input
            class="input input-bordered input-sm w-64"
            placeholder="Search by name, email, phone…"
            aria-label="Search contacts"
            value={q()}
            onInput={(e) => setQ(e.currentTarget.value)}
          />
          <select
            class="select select-bordered select-sm"
            aria-label="Filter by lifecycle stage"
            value={stage()}
            onChange={(e) => setStage(e.currentTarget.value)}
          >
            <option value="">All stages</option>
            <option value="lead">Lead</option>
            <option value="marketing_qualified">MQL</option>
            <option value="sales_qualified">SQL</option>
            <option value="opportunity">Opportunity</option>
            <option value="customer">Customer</option>
            <option value="evangelist">Evangelist</option>
            <option value="churned">Churned</option>
          </select>
          <input
            class="input input-bordered input-sm w-40"
            placeholder="Filter by tag"
            aria-label="Filter by tag"
            value={tagFilter()}
            onInput={(e) => setTagFilter(e.currentTarget.value)}
          />
          <Show when={tagFilter()}>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() => setTagFilter("")}
              aria-label="Clear tag filter"
            >
              ✕
            </button>
          </Show>
          <div class="w-60">
            <UserPicker
              wsKey={() => params().wsKey}
              value={ownerFilter}
              selectedName={ownerFilterName}
              onChange={(id, name) => {
                setOwnerFilter(id);
                setOwnerFilterName(name);
              }}
              placeholder="Filter by owner…"
              label="Filter by owner"
              size="sm"
            />
          </div>
          <Show when={me.data?.user_id && me.data.user_id !== ownerFilter()}>
            <button
              type="button"
              class="btn btn-ghost btn-sm"
              onClick={() => {
                setOwnerFilter(me.data!.user_id!);
                setOwnerFilterName(me.data?.display_name ?? me.data?.email ?? "Me");
              }}
            >
              Mine
            </button>
          </Show>
        </div>

        <ContactsBulkBar
          wsKey={() => params().wsKey}
          count={selection.count}
          rows={() => selection.selectedRows(visibleRows())}
          onClear={selection.clear}
        />

        <Show when={contacts.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
            Loading contacts…
          </div>
        </Show>
        <Show when={contacts.isError}>
          <div role="alert" class="alert alert-error">
            Failed to load contacts.
          </div>
        </Show>

        <Show when={contacts.data}>
          {(_data) => (
            <div class="overflow-x-auto bg-base-100 rounded-lg shadow-sm">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th class="w-8">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        aria-label="Select all contacts on this page"
                        checked={selection.allSelected()}
                        ref={(el) => {
                          if (el) el.indeterminate = selection.someSelected();
                        }}
                        onChange={(e) =>
                          e.currentTarget.checked ? selection.selectAll() : selection.clear()
                        }
                      />
                    </th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Stage</th>
                    <th title="AI-derived lead score (0-100)">Score</th>
                    <th>Tags</th>
                    <th>DNC</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={visibleRows()}>
                    {(c) => (
                      <tr class={`hover ${selection.has(c.id) ? "bg-base-200" : ""}`}>
                        <td>
                          <input
                            type="checkbox"
                            class="checkbox checkbox-sm"
                            aria-label={`Select ${c.display_name ?? c.full_name}`}
                            checked={selection.has(c.id)}
                            onChange={() => selection.toggle(c.id)}
                          />
                        </td>
                        <td>
                          <Link
                            to="/workspaces/$wsKey/contacts/$conId"
                            params={{ wsKey: params().wsKey, conId: c.id }}
                            class="link link-primary"
                          >
                            {c.display_name ?? c.full_name}
                          </Link>
                        </td>
                        <td class="text-sm opacity-80">{c.primary_email ?? "—"}</td>
                        <td>
                          <span class="badge badge-outline badge-sm">{c.lifecycle_stage}</span>
                        </td>
                        <td>
                          <LeadScoreBadge score={c.lead_score} size="sm" />
                        </td>
                        <td>
                          <TagBadges tags={() => c.tags} size="xs" />
                        </td>
                        <td>
                          <Show when={c.do_not_contact}>
                            <span class="badge badge-error badge-sm">DNC</span>
                          </Show>
                        </td>
                        <td class="text-xs opacity-60">
                          {new Date(c.updated_at).toLocaleDateString()}
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
              <Show when={visibleRows().length === 0}>
                <div class="text-center py-12 opacity-60">No contacts match.</div>
              </Show>
            </div>
          )}
        </Show>
        <Show when={contacts.hasNextPage}>
          <div class="flex justify-center py-2">
            <button
              type="button"
              class="btn btn-sm"
              disabled={contacts.isFetchingNextPage}
              onClick={() => contacts.fetchNextPage()}
            >
              {contacts.isFetchingNextPage ? "Loading…" : "Load more"}
            </button>
          </div>
        </Show>
      </div>

      <Show when={showCreate()}>
        <dialog open class="modal modal-open" aria-modal="true" aria-labelledby="new-contact-title">
          <div class="modal-box">
            <h3 id="new-contact-title" class="font-bold text-lg mb-4">
              New contact
            </h3>
            <form onSubmit={handleCreate} class="flex flex-col gap-3">
              <div class="grid grid-cols-2 gap-3">
                <input
                  name="first_name"
                  class="input input-bordered"
                  placeholder="First name"
                  aria-label="First name"
                />
                <input
                  name="last_name"
                  class="input input-bordered"
                  placeholder="Last name"
                  aria-label="Last name"
                />
              </div>
              <input
                name="primary_email"
                class="input input-bordered"
                placeholder="Email"
                aria-label="Email"
                type="email"
              />
              <input
                name="primary_phone"
                class="input input-bordered"
                placeholder="Phone"
                aria-label="Phone"
              />
              <select
                name="lifecycle_stage"
                class="select select-bordered"
                aria-label="Lifecycle stage"
              >
                <option value="lead">Lead</option>
                <option value="marketing_qualified">MQL</option>
                <option value="sales_qualified">SQL</option>
                <option value="opportunity">Opportunity</option>
                <option value="customer">Customer</option>
              </select>
              <div>
                <label class="label" for="new-contact-account">
                  <span class="label-text">Primary account (optional)</span>
                </label>
                <AccountPicker
                  wsKey={() => params().wsKey}
                  value={primaryAccountId}
                  selectedName={primaryAccountName}
                  onChange={(id, name) => {
                    setPrimaryAccountId(id);
                    setPrimaryAccountName(name);
                  }}
                  placeholder="Search accounts by name or domain…"
                  label="Primary account"
                />
              </div>
              <div>
                <label class="label">
                  <span class="label-text">Tags</span>
                </label>
                <TagsEditor
                  wsKey={() => params().wsKey}
                  entityType="contact"
                  value={createTags}
                  onChange={setCreateTags}
                />
              </div>
              <div>
                <label class="label">
                  <span class="label-text">Owner</span>
                </label>
                <UserPicker
                  wsKey={() => params().wsKey}
                  value={createOwner}
                  selectedName={createOwnerName}
                  onChange={(id, name) => {
                    setCreateOwner(id);
                    setCreateOwnerName(name);
                  }}
                />
              </div>
              <div class="modal-action">
                <button
                  type="button"
                  class="btn btn-ghost"
                  onClick={() => {
                    setShowCreate(false);
                    resetCreate();
                  }}
                >
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" disabled={createMutation.isPending}>
                  {createMutation.isPending ? "Saving…" : "Create"}
                </button>
              </div>
              <Show when={createMutation.isError}>
                <div class="alert alert-error text-sm">
                  {(createMutation.error as Error)?.message ?? "Failed"}
                </div>
              </Show>
            </form>
          </div>
        </dialog>
      </Show>
    </AppShell>
  );
}
